import * as React from 'react';
import { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';

import type SharedEntity from 'logic/permissions/SharedEntity';
import { Alert, Button } from 'components/bootstrap';
import { DEFAULT_PAGE_SIZES } from 'hooks/usePaginationQueryParameter';
import CollectionGranteesListItem from 'collections/components/CollectionGranteeListItem';
import {
  StyledGranteeList,
  GranteeListHeader,
  GranteeListPaginationWrapper,
  GranteeListStyledPagination,
  GranteeListStyledPageSizeSelect,
} from 'components/permissions/CommonStyledComponents';
import type { CollectionGrantees } from 'collections/types';
import useCollectionGrantees from 'collections/hooks/useCollectionGrantees';
import { Icon } from 'components/common';

type Props = {
  entityType: SharedEntity['type'];
  title: string;
  entityTypeTitle?: string | null | undefined;
  entityGRN: string;
};

const StyledDiv = styled.div`
  padding: 0;
  margin-bottom: ${(props) => props.theme.spacings.sm};
`;
const ToggleButton = styled(Button)`
  padding: 0;
  margin-bottom: ${(props) => props.theme.spacings.sm};
`;
const ToggleIcon = styled(Icon)`
  margin-left: ${(props) => props.theme.spacings.sm};
`;

const _paginatedGrantees = (selectedCollectionGrantees: CollectionGrantees, pageSize: number, currentPage: number) => {
  const begin = pageSize * (currentPage - 1);
  const end = begin + pageSize;

  return selectedCollectionGrantees.slice(begin, end);
};

const CollectionGranteesList = ({ entityType, entityTypeTitle = null, title, entityGRN }: Props) => {
  const { data } = useCollectionGrantees(entityGRN);
  const collectionGrantees: CollectionGrantees = data.list.map((dataGrantee) => ({
    granteeName: dataGrantee.granteeName,
    grantee: dataGrantee.grantee,
    collection_id: dataGrantee.collection_id,
    collection_name: dataGrantee.collection_name,
    collection_total: dataGrantee.collection_total,
    capability: dataGrantee.capability,
  }));
  const initialPageSize = DEFAULT_PAGE_SIZES[0];
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [currentPage, setCurrentPage] = useState(1);
  const paginatedGrantees = _paginatedGrantees(collectionGrantees, pageSize, currentPage);
  const hasGrantees =  useMemo(() => collectionGrantees.length > 0, [collectionGrantees]);
  const [showGranteeList, setShowGranteeList] = useState(false);
  const totalGrantees = collectionGrantees.length;
  const totalPages = Math.ceil(totalGrantees / pageSize);
  const showPageSizeSelect = totalGrantees > initialPageSize;

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    const newTotalPages = Math.ceil(totalPages / newSize);
    if (currentPage > newTotalPages) setCurrentPage(newTotalPages);
  };

  useEffect(() => {
    if (hasGrantees) {
      setShowGranteeList(true);
    }
  }, [hasGrantees]);

  return (
    <StyledDiv>
      <GranteeListHeader>
        <ToggleButton
          bsStyle="link"
          onClick={() => {
            setShowGranteeList((cur) => !cur);
          }}>
          {title}
          {showPageSizeSelect && (
            <GranteeListStyledPageSizeSelect
              onChange={(newPageSize) => handlePageSizeChange(newPageSize)}
              pageSize={pageSize}
            />
          )}
          <ToggleIcon name={showGranteeList ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} />
        </ToggleButton>
      </GranteeListHeader>
      {showGranteeList && (
        <>
          {hasGrantees ? (
            <StyledGranteeList>
              {paginatedGrantees.map((collectionGrantee) => (
                <CollectionGranteesListItem grantee={collectionGrantee} />
              ))}
            </StyledGranteeList>
          ) : (
            <Alert>This {entityTypeTitle || entityType} has no collection collaborators.</Alert>
          )}
          <GranteeListPaginationWrapper>
            <GranteeListStyledPagination totalPages={totalPages} currentPage={currentPage} onChange={setCurrentPage} />
          </GranteeListPaginationWrapper>
        </>
      )}
    </StyledDiv>
  );
};

export default CollectionGranteesList;
